import type { UrlObject } from 'url';
import { useRouter } from 'next/router';
import { chatHandoffLink } from '../../../../lib/chat-handoff';
import { useBasePath } from '../../../../lib/use-base-path';
import { ENTITY_TYPE, useGenericBi } from '../../../../lib/use-generic-bi';
import { markNavFromDrawer } from '../../../../lib/drawer-nav';
import { useLeaveChatOnNavigate } from '../../../../lib/use-leave-chat';
import { useTranslation } from '../../../../lib/i18n';
import { workspaceHasDashboard } from '../../../../lib/pages';
import { useWorkspaces, type WorkspaceSummary } from '../../../../lib/workspaces';
import { useHomepage } from '../../../HomepageContext';

export interface WorkspaceRowModel {
  ws: WorkspaceSummary;
  /** The Next page file, for `<Link href>`. */
  href: UrlObject;
  /** The URL the browser shows, for `<Link as>` — mount-prefixed. */
  as: string;
  active: boolean;
  onClick: () => void;
}

export interface WorkspaceRowsModel {
  ready: boolean;
  rows: WorkspaceRowModel[];
  /** Creating a workspace is an ASK — workspaces are agent-created, so the
   *  affordance opens a Maestro conversation rather than a form. */
  create: { route: string; path: string; onClick: () => void; label: string };
}

// The workspace whose screen the current route is on (any /workspace/[slug]* route,
// including drill-ins), so its row reads as active.
function useActiveWorkspaceSlug(): string | undefined {
  const router = useRouter();
  if (!router.pathname.startsWith('/workspace/[slug]')) return undefined;
  return typeof router.query.slug === 'string' ? router.query.slug : undefined;
}

// The caller's top-level workspaces as navigable rows: each goes straight into the
// workspace on its default tab — Growth & Goals (/workspace/[slug]) when it has a
// dashboard, else "Agents at work" (an empty Growth & Goals would be a dead landing).
// Shared by the wide rail's WorkspaceNav and the mobile drawer.
export function useWorkspaceRows(): WorkspaceRowsModel {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const state = useWorkspaces();
  const activeSlug = useActiveWorkspaceSlug();
  const leaveChat = useLeaveChatOnNavigate();
  const { logPageClickBi } = useGenericBi();
  const { state: homepage } = useHomepage();

  const createLink = chatHandoffLink({ kind: 'new', message: t('workspace.createPrompt') }, basePath);
  const create = {
    route: createLink.route,
    path: createLink.path,
    label: t('workspace.create'),
    onClick: () => {
      logPageClickBi({ page_name: 'menu', button_name: 'new_workspace', origin: 'menu' });
      leaveChat();
    },
  };

  if (state.status !== 'ready') return { ready: false, rows: [], create };

  const rows = state.workspaces.map((ws): WorkspaceRowModel => {
    const encoded = encodeURIComponent(ws.slug);
    const hasDashboard = workspaceHasDashboard(homepage, ws.slug) !== false;
    return {
      ws,
      href: hasDashboard
        ? { pathname: '/workspace/[slug]', query: { slug: ws.slug } }
        : { pathname: '/workspace/[slug]/agents', query: { slug: ws.slug } },
      as: hasDashboard ? `${basePath}/workspace/${encoded}` : `${basePath}/workspace/${encoded}/agents`,
      active: activeSlug === ws.slug,
      onClick: () => {
        markNavFromDrawer();
        // Same entity as the Home strip's open-workspace click — `ws.id`, matching native.
        logPageClickBi(
          { page_name: 'menu', button_name: 'open_workspace', origin: 'menu' },
          { componentName: ws.slug, entity: { id: ws.id, type: ENTITY_TYPE.workspace } },
        );
        leaveChat();
      },
    };
  });

  return { ready: true, rows, create };
}
